import {motion, useInView} from 'motion/react';
import {useRef} from 'react';
import {Code2, Database, Rocket, Users} from 'lucide-react';

export function About() {
    const ref = useRef(null);
    const isInView = useInView(ref, {once: true, amount: 0.3});

    const highlights = [
        {
            icon: Code2,
            title: 'Clean Code',
            description: 'Writing maintainable and scalable code following best practices',
        },
        {
            icon: Database,
            title: 'Full Stack',
            description: 'Expertise in both frontend and backend technologies',
        },
        {
            icon: Rocket,
            title: 'Performance',
            description: 'Optimizing applications for speed and efficiency',
        },
        {
            icon: Users,
            title: 'Collaboration',
            description: 'Strong team player with excellent communication skills',
        },
    ];

    return (
        <section id="about" className="py-32 px-6 relative overflow-hidden" ref={ref}>
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{opacity: 0, y: 30}}
                    animate={isInView ? {opacity: 1, y: 0} : {}}
                    transition={{duration: 0.6}}
                    className="text-center mb-16"
                >
                    <h2 className="mb-4">
                        About <span
                        className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Me</span>
                    </h2>
                    <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto"/>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{opacity: 0, x: -50}}
                        animate={isInView ? {opacity: 1, x: 0} : {}}
                        transition={{duration: 0.6, delay: 0.2}}
                        style={{
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >

                        <div style={{
                            position: 'relative',
                            width: "100%",
                            height: 350,
                            display: 'flex',
                            justifyContent: "center",
                        }}>
                            <div style={{
                                position: 'absolute',
                                width: 300,
                                height: 300,
                                borderRadius: 400,
                                overflow: 'hidden',
                            }}>
                                <img
                                    style={{
                                        position: 'absolute',
                                        top:20,
                                        scale:1.1,
                                        objectFit: 'cover',
                                    }} src={require("../assets/dev_picture.jpg")}/>
                            </div>
                        </div>


                        <div
                            className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-lg mb-6">
                            <p className="text-cyan-400">Founder, CEO & Lead Developer at ZeeJay Labs</p>
                        </div>

                        <p className="text-slate-300 mb-6">
                                As the founder and CEO of ZeeJay Labs, I lead a cutting-edge technology startup
                                focused on building innovative web applications and digital solutions. Combining
                                business leadership with hands-on technical expertise, I transform ideas into
                                robust, scalable applications that solve real-world problems.
                            </p>
                            <p className="text-slate-300 mb-6">
                                My journey spans both entrepreneurship and software development, giving me a unique
                                perspective on creating products that are not only technically excellent but also
                                strategically positioned for market success. I thrive on challenges and continuously
                                evolve with the ever-changing tech landscape.
                            </p>
                            <p className="text-slate-300">
                                Whether leading a team, architecting complex systems, or coding innovative features,
                                I bring the same level of passion and excellence to every aspect of building
                                exceptional digital experiences.
                            </p>
                    </motion.div>

                    <motion.div
                        initial={{opacity: 0, x: 50}}
                        animate={isInView ? {opacity: 1, x: 0} : {}}
                        transition={{duration: 0.6, delay: 0.4}}
                        className="grid grid-cols-2 gap-6"
                    >
                        {highlights.map((item, index) => (
                            <motion.div
                                key={item.title}
                                initial={{opacity: 0, y: 20}}
                                animate={isInView ? {opacity: 1, y: 0} : {}}
                                transition={{duration: 0.6, delay: 0.2 * index}}
                                className="group relative"
                            >
                                {/* Glow effect */}
                                <div
                                    className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"/>

                                <div
                                    className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/50 transition-all hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2">
                                    <div
                                        className="p-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl inline-block mb-4 group-hover:scale-110 transition-transform">
                                        <item.icon className="text-cyan-400" size={32}/>
                                    </div>
                                    <h3 className="text-slate-100 mb-2">{item.title}</h3>
                                    <p className="text-slate-400 text-sm">{item.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    );
}